import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  timesheetsApi,
  Timesheet,
  GroupNode,
  GroupByField,
  GroupedTimesheetsParams,
} from '../../../api/timesheetsApi';
import { departmentsApi } from '../../../api/departmentsApi';
import { projectsApi, Project } from '../../../api/projectsApi';
import { usersApi } from '../../../api/usersApi';
import { taskTypesApi, TaskType } from '../../../api/taskTypesApi';
import { useAuth } from '../../../context/AuthContext';
import { downloadBlob } from '../../../utils/helpers';
import {
  Search,
  Download,
  FileSpreadsheet,
  Refresh,
  ChevronRight,
  ChevronDown,
  X,
  Filter,
  StarFilled,
  Columns,
} from '../Icons';
import { Button } from '../Button';
import { SortIcon } from '../SortIcon';
import { useSorting, applySorting } from '../../../hooks/useSorting';
import { FavoritesDropdown } from '../FavoritesDropdown';
import { FavoriteConfig } from '../../../api/favoritesApi';
import { ColumnSearchBar, SearchChip } from '../ColumnSearchBar';
import './GroupedTimesheets.css';

/* ──────────────────────────────────────────────
   Column Definitions for table display
   ────────────────────────────────────────────── */

interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible: boolean;
}

const TIMESHEET_COLUMNS: ColumnConfig[] = [
  { key: 'date', label: 'Date', defaultVisible: true },
  { key: 'day', label: 'Day', defaultVisible: false },
  { key: 'employee', label: 'Employee', defaultVisible: true },
  { key: 'employee_email', label: 'Employee Email', defaultVisible: false },
  { key: 'employee_code', label: 'Code', defaultVisible: false },
  { key: 'accounts', label: 'Accounts', defaultVisible: false },
  { key: 'department', label: 'Department', defaultVisible: false },
  { key: 'practice', label: 'Practice', defaultVisible: false },
  { key: 'project', label: 'Project', defaultVisible: true },
  { key: 'project_manager', label: 'Project Manager', defaultVisible: false },
  { key: 'project_category', label: 'Project Category', defaultVisible: false },
  { key: 'resource_category', label: 'Resource Category', defaultVisible: false },
  { key: 'tasks', label: 'Tasks', defaultVisible: true },
  { key: 'sub_tasks', label: 'Sub Tasks', defaultVisible: false },
  { key: 'description', label: 'Description', defaultVisible: true },
  { key: 'hours', label: 'Hours Spent', defaultVisible: true },
  { key: 'reject_reason', label: 'Reject Reason', defaultVisible: false },
  { key: 'status', label: 'Status', defaultVisible: false },
  { key: 'active_status', label: 'Active/Inactive', defaultVisible: false },
];

const DEFAULT_VISIBLE_COLUMNS = TIMESHEET_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

// v5: bump key to invalidate stale persisted columns from previous defaults
const STORAGE_KEY_COLUMNS = 'gt_visible_columns_v5';

const TIMESHEET_SEARCH_FIELDS = [
  { key: 'employee', label: 'Employee' },
  { key: 'project', label: 'Project' },
  { key: 'task', label: 'Tasks' },
  { key: 'description', label: 'Description' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'project_manager', label: 'Project Manager' },
];

/* ──────────────────────────────────────────────
   Group By field definitions
   ────────────────────────────────────────────── */

interface GroupByOption {
  key: GroupByField;
  label: string;
  /** only these roles see this option; undefined = all */
  roles?: string[];
  /** if true, this option has a date sub-menu */
  hasDateSubmenu?: boolean;
}

/** Date sub-options for the "Date" group by */
interface DateSubOption {
  key: GroupByField;
  label: string;
}

const DATE_SUB_OPTIONS: DateSubOption[] = [
  { key: 'date', label: 'Date' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
];

/** All date-related group keys */
const DATE_GROUP_KEYS: GroupByField[] = ['date', 'day', 'week', 'month', 'quarter', 'year'];

/** Primary group by options (shown in main menu) */
const GROUP_BY_OPTIONS: GroupByOption[] = [
  { key: 'project', label: 'Project' },
  { key: 'date', label: 'Date', hasDateSubmenu: true },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'employee', label: 'Employee' },
  { key: 'project_manager', label: 'Project Manager' },
  { key: 'status', label: 'Status' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'billable', label: 'Billable / Non-Billable Project' },
  { key: 'project_type', label: 'Project Category' },
  { key: 'employee_category', label: 'Resource Category' },
  { key: 'employee_practice', label: 'Employee Practice' },
  { key: 'active_inactive', label: 'Active/Inactive Employee' },
];

/** All columns available for custom grouping (includes everything) */
const ALL_GROUP_COLUMNS: GroupByOption[] = [
  { key: 'project', label: 'Project' },
  { key: 'date', label: 'Date', hasDateSubmenu: true },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'employee', label: 'Employee' },
  { key: 'project_manager', label: 'Project Manager' },
  { key: 'status', label: 'Status' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'billable', label: 'Billable / Non-Billable Project' },
  { key: 'project_type', label: 'Project Category' },
  { key: 'employee_category', label: 'Resource Category' },
  { key: 'employee_practice', label: 'Employee Practice' },
  { key: 'active_inactive', label: 'Active/Inactive Employee' },
];

/** Helper to get label for any group-by key */
function getGroupByLabel(key: GroupByField): string {
  return ALL_GROUP_COLUMNS.find((o) => o.key === key)?.label ?? key;
}

/* ──────────────────────────────────────────────
   Props
   ────────────────────────────────────────────── */

interface GroupedTimesheetsProps {
  scope: 'all' | 'team';
  pageTitle: string;
}

/* ──────────────────────────────────────────────
   Component
   ────────────────────────────────────────────── */

export function GroupedTimesheets({ scope, pageTitle }: GroupedTimesheetsProps) {
  const { user } = useAuth();
  const roles = user?.roles ?? [];

  // Group-by state
  const [activeGroupBy, setActiveGroupBy] = useState<GroupByField[]>([]);
  const [groups, setGroups] = useState<GroupNode[]>([]);
  const [grandTotalHours, setGrandTotalHours] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Detail rows (loaded on expand for leaf groups)
  const [detailRows, setDetailRows] = useState<Record<string, { timesheets: Timesheet[]; total: number; page: number; pages: number }>>({});
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());

  // Filters
  const [searchChips, setSearchChips] = useState<SearchChip[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [projectFilter, setProjectFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [taskTypeFilter, setTaskTypeFilter] = useState('');
  const [billableFilter, setBillableFilter] = useState('');
  const [employeeCategoryFilter, setEmployeeCategoryFilter] = useState('');
  const [employeePracticeFilter, setEmployeePracticeFilter] = useState('');
  const [activeInactiveFilter, setActiveInactiveFilter] = useState('active');
  const [showFilters, setShowFilters] = useState(false);

  // Active favorite
  const [activeFavoriteName, setActiveFavoriteName] = useState<string | null>(null);

  // Filter data (loaded from APIs)
  const [filterProjects, setFilterProjects] = useState<{ id: number; name: string }[]>([]);
  const [filterEmployees, setFilterEmployees] = useState<{ id: number; name: string; category?: string; practice?: string }[]>([]);
  const [filterDepartments, setFilterDepartments] = useState<{ id: number; name: string }[]>([]);
  const [filterManagers, setFilterManagers] = useState<{ id: number; name: string }[]>([]);
  const [filterTaskTypes, setFilterTaskTypes] = useState<{ id: number; name: string }[]>([]);

  // Export
  const [exportLoading, setExportLoading] = useState(false);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY_COLUMNS}_${scope}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef<HTMLTableHeaderCellElement>(null);

  // Dropdowns
  const [showGroupByMenu, setShowGroupByMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDateSubmenu, setShowDateSubmenu] = useState(false);
  const [showCustomGroupMenu, setShowCustomGroupMenu] = useState(false);
  const groupByMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Flat timesheets (when no group-by selected)
  const [flatTimesheets, setFlatTimesheets] = useState<Timesheet[]>([]);
  const [flatTotal, setFlatTotal] = useState(0);
  const [flatPage, setFlatPage] = useState(1);
  const [flatPages, setFlatPages] = useState(1);
  const PER_PAGE = 50;

  // Sorting state for flat view
  type FlatTimesheetSortKey = 'date' | 'day' | 'employee' | 'project' | 'tasks' | 'description' | 'hours' | 'status' | 'active_status';
  const { sortConfig: flatSortConfig, handleSort: handleFlatSort, getSortDirection, isColumnSorted } = useSorting<FlatTimesheetSortKey>();

  // Apply sorting to flat timesheets
  const sortedFlatTimesheets = useMemo(() => {
    return applySorting(flatTimesheets, flatSortConfig, (timesheet, key) => {
      switch (key) {
        case 'date': return timesheet.date ? new Date(timesheet.date).getTime() : 0;
        case 'day': return timesheet.date ? new Date(timesheet.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }) : '';
        case 'employee': return timesheet.employee_name || '';
        case 'project': return timesheet.project_name || '';
        case 'tasks': return timesheet.task_type_name || '';
        case 'description': return timesheet.description || '';
        case 'hours': return timesheet.hours || 0;
        case 'status': return timesheet.status || '';
        case 'active_status': return (timesheet as any).employee_is_active ? 1 : 0;
        default: return '';
      }
    });
  }, [flatTimesheets, flatSortConfig]);

  // Close menus on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (groupByMenuRef.current && !groupByMenuRef.current.contains(e.target as Node)) {
        setShowGroupByMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setShowColumnMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save visible columns to localStorage when changed
  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_COLUMNS}_${scope}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, scope]);

  // Column toggle handlers
  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        // Don't allow deselecting all columns
        if (prev.length === 1) return prev;
        return prev.filter(c => c !== columnKey);
      }
      return [...prev, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setVisibleColumns(TIMESHEET_COLUMNS.map(c => c.key));
  };

  const handleResetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  // Available options (filter already-selected; hide Date parent if any date sub-key active)
  const availableOptions = useMemo(
    () => GROUP_BY_OPTIONS.filter((o) => {
      if (activeGroupBy.includes(o.key)) return false;
      if (o.roles && !o.roles.some((r) => roles.includes(r))) return false;
      // Hide "Date" parent if ANY date sub-option is already selected
      if (o.hasDateSubmenu && DATE_GROUP_KEYS.some((dk) => activeGroupBy.includes(dk))) return false;
      return true;
    }),
    [activeGroupBy, roles],
  );

  /* ── Load filter dropdown data ── */
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [projRes, empRes, deptRes, mgrRes, taskRes] = await Promise.all([
          projectsApi.getProjects({ per_page: 500 }),
          usersApi.getUsers({ per_page: 500, is_active: 'true' }),
          departmentsApi.getDepartments(),
          usersApi.getManagers(),
          taskTypesApi.getTaskTypes(),
        ]);
        setFilterProjects((projRes as any).projects?.map((p: any) => ({ id: p.id, name: p.name })) ?? []);
        setFilterEmployees((empRes as any).users?.map((u: any) => ({ id: u.id, name: u.name, category: u.category, practice: u.practice })) ?? []);
        setFilterDepartments((deptRes as any).departments?.map((d: any) => ({ id: d.id, name: d.name })) ?? (Array.isArray(deptRes) ? (deptRes as any).map((d: any) => ({ id: d.id, name: d.name })) : []));
        setFilterManagers(Array.isArray(mgrRes) ? (mgrRes as any).map((m: any) => ({ id: m.id, name: m.name })) : (mgrRes as any).managers?.map((m: any) => ({ id: m.id, name: m.name })) ?? []);
        setFilterTaskTypes(Array.isArray(taskRes) ? (taskRes as any).map((t: any) => ({ id: t.id, name: t.name })) : (taskRes as any).task_types?.map((t: any) => ({ id: t.id, name: t.name })) ?? []);
      } catch {
        // silently fail — filters will just be empty
      }
    };
    loadFilterData();
  }, []);

  /* ── Build common filter params ── */
  const buildFilterParams = useCallback(() => {
    const params: any = {};
    // Column-specific search params
    const colSearches: Record<string, string[]> = {};
    searchChips.forEach(chip => {
      if (!colSearches[chip.field]) colSearches[chip.field] = [];
      colSearches[chip.field].push(chip.value);
    });
    Object.entries(colSearches).forEach(([field, values]) => {
      params[`search_${field}`] = values.join('||');
    });
    if (statusFilter) params.status = statusFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (projectFilter) params.project_id = Number(projectFilter);
    if (employeeFilter) params.employee_id = Number(employeeFilter);
    if (departmentFilter) params.department_id = Number(departmentFilter);
    if (managerFilter) params.manager_id = Number(managerFilter);
    if (taskTypeFilter) params.task_type_id = Number(taskTypeFilter);
    if (billableFilter) params.billable = billableFilter;
    if (employeeCategoryFilter) params.employee_category = employeeCategoryFilter;
    if (employeePracticeFilter) params.employee_practice = employeePracticeFilter;
    if (activeInactiveFilter) params.active_inactive = activeInactiveFilter;
    return params;
  }, [searchChips, statusFilter, startDate, endDate, projectFilter, employeeFilter, departmentFilter, managerFilter, taskTypeFilter, billableFilter, employeeCategoryFilter, employeePracticeFilter, activeInactiveFilter]);

  /* ── Fetch grouped data ── */
  const fetchGroupedData = useCallback(async () => {
    if (activeGroupBy.length === 0) return;
    try {
      setLoading(true);
      setError(null);
      const params: GroupedTimesheetsParams = {
        group_by: activeGroupBy.join(','),
        scope,
        ...buildFilterParams(),
      };
      const res = await timesheetsApi.getGroupedTimesheets(params);
      setGroups(res.groups);
      setGrandTotalHours(res.grand_total_hours);
      setTotalCount(res.total);
      // Clear expanded + detail rows
      setExpandedGroups(new Set());
      setDetailRows({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeGroupBy, scope, buildFilterParams]);

  /* ── Fetch flat data (no grouping) ── */
  const fetchFlatData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const res = await timesheetsApi.getFilteredTimesheets({
        page,
        per_page: PER_PAGE,
        ...buildFilterParams(),
      });
      setFlatTimesheets(res.timesheets);
      setFlatTotal(res.total);
      setFlatPage(res.page ?? 1);
      setFlatPages(res.pages ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  /* ── Trigger fetch when group-by or filters change ── */
  useEffect(() => {
    if (activeGroupBy.length > 0) {
      fetchGroupedData();
    } else {
      fetchFlatData(1);
    }
  // eslint-disable-next-line
  }, [activeGroupBy, searchChips, statusFilter, startDate, endDate, projectFilter, employeeFilter, departmentFilter, managerFilter, taskTypeFilter, billableFilter, employeeCategoryFilter, employeePracticeFilter, activeInactiveFilter]);

  /* ── Add / remove group by field ── */
  const addGroupByField = (field: GroupByField) => {
    setActiveGroupBy((prev) => [...prev, field]);
    setShowGroupByMenu(false);
  };

  const removeGroupByField = (field: GroupByField) => {
    setActiveGroupBy((prev) => {
      const idx = prev.indexOf(field);
      if (idx === -1) return prev;
      // Remove this field and all fields after it
      return prev.slice(0, idx);
    });
  };

  /* ── Toggle expand ── */
  const toggleExpand = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  /* ── Load detail rows for a leaf group ── */
  const loadDetailRows = useCallback(
    async (groupPath: string, filterOverrides: Record<string, any>, page = 1) => {
      const loadKey = `${groupPath}:${page}`;
      setDetailLoading((prev) => new Set(prev).add(groupPath));
      try {
        const res = await timesheetsApi.getFilteredTimesheets({
          page,
          per_page: 80,
          ...buildFilterParams(),
          ...filterOverrides,
        });
        setDetailRows((prev) => ({
          ...prev,
          [groupPath]: {
            timesheets: res.timesheets,
            total: res.total,
            page: res.page ?? 1,
            pages: res.pages ?? 1,
          },
        }));
      } catch {
        // silently fail
      } finally {
        setDetailLoading((prev) => {
          const next = new Set(prev);
          next.delete(groupPath);
          return next;
        });
      }
    },
    [buildFilterParams],
  );

  /* ── Build filter overrides from a group node path ── */
  const buildFilterOverrides = (node: GroupNode, parentOverrides: Record<string, any> = {}): Record<string, any> => {
    const overrides = { ...parentOverrides };
    const fieldMap: Record<string, string> = {
      project: 'project_id',
      employee: 'employee_id',
      date: 'start_date',
      day: 'group_day',
      week: 'group_week',
      month: 'group_month',
      quarter: 'group_quarter',
      year: 'group_year',
      department: 'department_id',
      manager: 'manager_id',
      project_manager: 'project_manager_id',
      status: 'status',
      tasks: 'task_type_id',
      billable: 'billable',
      employee_category: 'employee_category',
      employee_practice: 'employee_practice',
      active_inactive: 'active_inactive',
      project_type: 'project_type',
    };
    const paramKey = fieldMap[node.field];
    if (paramKey) {
      if (node.field === 'date' && node.field_value) {
        overrides.start_date = node.field_value;
        overrides.end_date = node.field_value;
      } else if (node.field === 'week' && node.field_value) {
        // week field_value is YYYY-MM-DD (Monday of that week)
        const weekStart = node.field_value;
        const ws = new Date(weekStart + 'T00:00:00');
        const we = new Date(ws);
        we.setDate(we.getDate() + 6);
        overrides.start_date = weekStart;
        overrides.end_date = we.toISOString().slice(0, 10);
      } else if (node.field === 'month' && node.field_value) {
        // field_value is YYYY-MM
        overrides.start_date = node.field_value + '-01';
        const [y, m] = node.field_value.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        overrides.end_date = `${node.field_value}-${String(lastDay).padStart(2, '0')}`;
      } else if (node.field === 'quarter' && node.field_value) {
        // field_value is YYYY-QN
        const parts = node.field_value.split('-Q');
        if (parts.length === 2) {
          const yr = parseInt(parts[0]);
          const q = parseInt(parts[1]);
          const startMonth = (q - 1) * 3 + 1;
          overrides.start_date = `${yr}-${String(startMonth).padStart(2, '0')}-01`;
          const endMonth = q * 3;
          const lastDay = new Date(yr, endMonth, 0).getDate();
          overrides.end_date = `${yr}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        }
      } else if (node.field === 'year' && node.field_value) {
        const yr = parseInt(String(node.field_value));
        overrides.start_date = `${yr}-01-01`;
        overrides.end_date = `${yr}-12-31`;
      } else if (node.field === 'day') {
        // Day name grouping - can't easily filter, leave as-is
      } else if (node.field === 'billable') {
        overrides.billable = node.field_value === 'True' ? 'true' : 'false';
      } else if (node.field === 'active_inactive') {
        overrides.active_inactive = node.field_value === 'True' ? 'active' : 'inactive';
      } else {
        overrides[paramKey] = node.field_value;
      }
    }
    return overrides;
  };



  /* ── Export ── */
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const blob = await timesheetsApi.exportTimesheets({
        scope,
        group_by: activeGroupBy.length > 0 ? activeGroupBy.join(',') : undefined,
        columns: visibleColumns.join(','),
        ...buildFilterParams(),
      });
      const filename = activeGroupBy.length > 0
        ? `timesheets_grouped_${new Date().toISOString().slice(0, 10)}.xlsx`
        : `timesheets_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlob(blob, filename);
    } catch {
      // ignore
    } finally {
      setExportLoading(false);
      setShowExportMenu(false);
    }
  };

  /* ── Render a group node recursively ── */
  const renderGroupNode = (
    node: GroupNode,
    depth: number,
    parentOverrides: Record<string, any> = {},
  ): React.ReactNode => {
    const isExpanded = expandedGroups.has(node.key);
    const hasSubgroups = node.subgroups && node.subgroups.length > 0;
    const isLeaf = !hasSubgroups;
    const overrides = buildFilterOverrides(node, parentOverrides);
    const groupPath = node.key;

    const handleToggle = () => {
      toggleExpand(node.key);
      // If leaf group is being expanded and no detail rows yet, fetch
      if (isLeaf && !isExpanded && !detailRows[groupPath]) {
        loadDetailRows(groupPath, overrides, 1);
      }
    };

    const detailData = detailRows[groupPath];
    const isDetailLoading = detailLoading.has(groupPath);

    return (
      <React.Fragment key={node.key}>
        {/* Group header row */}
        <tr
          className={`group-header-row depth-${Math.min(depth, 3)}`}
          onClick={handleToggle}
          style={{ cursor: 'pointer' }}
        >
          <td colSpan={Math.max(visibleColumns.length - 2, 1)} style={{ paddingLeft: `${depth * 24 + 12}px` }}>
            <span className="group-expand-icon">
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </span>
            <span className="group-label">{node.label}</span>
            <span className="group-count">({node.count})</span>
            {node.count > 80 && hasSubgroups === false && (
              <span className="group-pagination-hint">
                1-80 / {node.count} {'< >'}
              </span>
            )}
          </td>
          <td className="group-hours-cell">{node.total_hours.toFixed(2)}</td>
          <td></td>
          <td className="gt-td-settings"></td>
        </tr>

        {/* Sub-groups */}
        {isExpanded && hasSubgroups && node.subgroups!.map((sub) => renderGroupNode(sub, depth + 1, overrides))}

        {/* Detail rows (leaf) */}
        {isExpanded && isLeaf && (
          <>
            {isDetailLoading && (
              <tr className="detail-loading-row">
                <td colSpan={visibleColumns.length + 1}>Loading...</td>
              </tr>
            )}
            {detailData && detailData.timesheets.map((t) => (
              <tr key={t.id} className="detail-row" style={{ paddingLeft: `${(depth + 1) * 24}px` }}>
                {visibleColumns.includes('date') && <td>{t.date ? formatDisplayDate(t.date) : ''}</td>}
                {visibleColumns.includes('day') && <td>{t.date ? getDayName(t.date) : ''}</td>}
                {visibleColumns.includes('employee') && <td>{t.employee_name}</td>}
                {visibleColumns.includes('employee_email') && <td className="email-cell">{t.employee_email || '-'}</td>}
                {visibleColumns.includes('employee_code') && <td>{t.employee_code || '-'}</td>}
                {visibleColumns.includes('accounts') && <td>{t.client_name || '-'}</td>}
                {visibleColumns.includes('department') && <td>{(t as any).department_name || '-'}</td>}
                {visibleColumns.includes('practice') && <td>{(t as any).employee_practice || '-'}</td>}
                {visibleColumns.includes('project') && <td>{t.project_name}</td>}
                {visibleColumns.includes('project_manager') && <td>{(t as any).project_manager_name || '-'}</td>}
                {visibleColumns.includes('project_category') && <td>{(t as any).project_type || '-'}</td>}
                {visibleColumns.includes('resource_category') && <td>{(t as any).employee_category || '-'}</td>}
                {visibleColumns.includes('tasks') && <td className="tasks-cell">{t.task_type_name}</td>}
                {visibleColumns.includes('sub_tasks') && <td>-</td>}
                {visibleColumns.includes('description') && (
                  <td className="description-cell" title={t.description || ''}>
                    {t.description ? (t.description.length > 60 ? t.description.slice(0, 60) + '...' : t.description) : ''}
                  </td>
                )}
                {visibleColumns.includes('hours') && (
                  <td className="hours-cell">
                    {t.hours.toFixed(2)}
                  </td>
                )}
                {visibleColumns.includes('reject_reason') && <td>-</td>}
                {visibleColumns.includes('status') && (
                  <td className="status-cell">
                    <span className={`status-badge status-${t.status}`}>{t.status}</span>
                  </td>
                )}
                {visibleColumns.includes('active_status') && (
                  <td>{(t as any).employee_is_active === false ? 'Inactive' : 'Active'}</td>
                )}
                <td className="gt-td-settings"></td>
              </tr>
            ))}
            {detailData && detailData.pages > 1 && (              <tr className="detail-pagination-row">
                <td colSpan={visibleColumns.length + 1}>
                  <div className="detail-pagination">
                    <span>
                      {((detailData.page - 1) * 80) + 1}-{Math.min(detailData.page * 80, detailData.total)} / {detailData.total}
                    </span>
                    <button
                      disabled={detailData.page <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        loadDetailRows(groupPath, overrides, detailData.page - 1);
                      }}
                    >
                      ‹
                    </button>
                    <button
                      disabled={detailData.page >= detailData.pages}
                      onClick={(e) => {
                        e.stopPropagation();
                        loadDetailRows(groupPath, overrides, detailData.page + 1);
                      }}
                    >
                      ›
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </>
        )}
      </React.Fragment>
    );
  };

  /* ──────────────────────────────────────────────
     RENDER
     ────────────────────────────────────────────── */

  return (
    <div className="grouped-timesheets-page">
      {/* Page header */}
      <div className="gt-page-header">
        <h1>{pageTitle}</h1>
      </div>

      {/* Active group-by chips + toolbar */}
      <div className="gt-toolbar">
        <div className="gt-toolbar-left">
          {/* Active favorite chip */}
          {activeFavoriteName && (
            <span className="gt-fav-chip">
              <StarFilled className="gt-fav-chip-star" />
              {activeFavoriteName}
              <button
                className="gt-fav-chip-remove"
                onClick={() => {
                  setActiveFavoriteName(null);
                  setSearchChips([]);
                  setActiveGroupBy([]);
                  setStatusFilter('');
                  const n = new Date();
                  const y = n.getFullYear();
                  const m = n.getMonth() + 1;
                  setStartDate(`${y}-${String(m).padStart(2, '0')}-01`);
                  const ld = new Date(y, m, 0).getDate();
                  setEndDate(`${y}-${String(m).padStart(2, '0')}-${String(ld).padStart(2, '0')}`);
                  setProjectFilter('');
                  setEmployeeFilter('');
                  setDepartmentFilter('');
                  setManagerFilter('');
                  setTaskTypeFilter('');
                  setBillableFilter('');
                  setEmployeeCategoryFilter('');
                  setEmployeePracticeFilter('');
                  setActiveInactiveFilter('active');
                }}
                title="Remove favorite"
              >
                <X />
              </button>
            </span>
          )}

          {/* Active group-by chips */}
          {activeGroupBy.length > 0 && (
            <div className="gt-groupby-chips">
              <span className="gt-chip-icon">⊞</span>
              {activeGroupBy.map((field, idx) => (
                <React.Fragment key={field}>
                  {idx > 0 && <span className="gt-chip-separator">&gt;</span>}
                  <span className="gt-chip">
                    {getGroupByLabel(field)}
                    <button
                      className="gt-chip-remove"
                      onClick={() => removeGroupByField(field)}
                      title="Remove"
                    >
                      <X />
                    </button>
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Search */}
          <ColumnSearchBar
            fields={TIMESHEET_SEARCH_FIELDS}
            chips={searchChips}
            onChipsChange={setSearchChips}
            placeholder="Search timesheets..."
          />
        </div>

        <div className="gt-toolbar-right">
          {/* Filters toggle */}
          <Button
            variant={showFilters ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter />
            Filters
          </Button>

          {/* Group By dropdown */}
          <div className="gt-dropdown-container" ref={groupByMenuRef}>
            <Button variant="ghost" size="sm" onClick={() => { setShowGroupByMenu(!showGroupByMenu); setShowDateSubmenu(false); setShowCustomGroupMenu(false); }}>
              ⊞ Group By
            </Button>
            {showGroupByMenu && (
              <div className="gt-dropdown-menu gt-groupby-menu">
                {availableOptions.length === 0 && !showCustomGroupMenu ? (
                  <div className="gt-dropdown-empty">All fields selected</div>
                ) : (
                  <>
                    {availableOptions.map((opt) => (
                      <div key={opt.key} className="gt-dropdown-item-wrapper">
                        {opt.hasDateSubmenu ? (
                          <button
                            className="gt-dropdown-item gt-has-submenu"
                            onMouseEnter={() => setShowDateSubmenu(true)}
                            onMouseLeave={() => setShowDateSubmenu(false)}
                          >
                            {opt.label}
                            <ChevronRight className="gt-submenu-arrow" />
                            {showDateSubmenu && (
                              <div className="gt-submenu" onMouseEnter={() => setShowDateSubmenu(true)} onMouseLeave={() => setShowDateSubmenu(false)}>
                                {DATE_SUB_OPTIONS.filter(d => !activeGroupBy.includes(d.key)).map((sub) => (
                                  <button
                                    key={sub.key}
                                    className="gt-dropdown-item"
                                    onClick={(e) => { e.stopPropagation(); addGroupByField(sub.key); setShowDateSubmenu(false); }}
                                  >
                                    {sub.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </button>
                        ) : (
                          <button
                            className="gt-dropdown-item"
                            onClick={() => addGroupByField(opt.key)}
                          >
                            {opt.label}
                          </button>
                        )}
                      </div>
                    ))}
                    {/* Add Custom Group */}
                    <div className="gt-dropdown-divider" />
                    <div className="gt-dropdown-item-wrapper">
                      <button
                        className="gt-dropdown-item gt-has-submenu"
                        onMouseEnter={() => setShowCustomGroupMenu(true)}
                        onMouseLeave={() => setShowCustomGroupMenu(false)}
                      >
                        Add Custom Group
                        <ChevronRight className="gt-submenu-arrow" />
                        {showCustomGroupMenu && (
                          <div className="gt-submenu gt-custom-submenu" onMouseEnter={() => setShowCustomGroupMenu(true)} onMouseLeave={() => setShowCustomGroupMenu(false)}>
                            {ALL_GROUP_COLUMNS.filter(c => !activeGroupBy.includes(c.key)).map((col) => (
                              <button
                                key={col.key}
                                className="gt-dropdown-item"
                                onClick={(e) => { e.stopPropagation(); addGroupByField(col.key); setShowCustomGroupMenu(false); }}
                              >
                                {col.label}
                              </button>
                            ))}
                            {ALL_GROUP_COLUMNS.filter(c => !activeGroupBy.includes(c.key)).length === 0 && (
                              <div className="gt-dropdown-empty">All columns selected</div>
                            )}
                          </div>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Export */}
          <div className="gt-dropdown-container" ref={exportMenuRef}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportLoading}
            >
              <Download />
              Export
            </Button>
            {showExportMenu && (
              <div className="gt-dropdown-menu gt-export-menu">
                <button className="gt-dropdown-item" onClick={handleExport}>
                  <FileSpreadsheet />
                  Export as Excel
                </button>
              </div>
            )}
          </div>

          {/* Favorites */}
          <FavoritesDropdown
            pageKey={scope === 'all' ? 'all_timesheets' : 'my_project_team'}
            getCurrentConfig={() => ({
              column_searches: searchChips.map(c => ({ field: c.field, label: c.label, value: c.value })),
              group_by: activeGroupBy as string[],
              visible_columns: visibleColumns,
              filters: {
                status: statusFilter,
                start_date: startDate,
                end_date: endDate,
                project_id: projectFilter,
                employee_id: employeeFilter,
                department_id: departmentFilter,
                manager_id: managerFilter,
                task_type_id: taskTypeFilter,
                billable: billableFilter,
                employee_category: employeeCategoryFilter,
                employee_practice: employeePracticeFilter,
                active_inactive: activeInactiveFilter,
              },
            })}
            applyConfig={(cfg: FavoriteConfig) => {
              if (cfg.column_searches) {
                setSearchChips(cfg.column_searches.map(c => ({ id: Math.random().toString(36).slice(2, 11), ...c })));
              } else {
                setSearchChips([]);
              }
              setActiveGroupBy((cfg.group_by || []) as GroupByField[]);
              if (cfg.visible_columns && cfg.visible_columns.length > 0) {
                setVisibleColumns(cfg.visible_columns);
              }
              const f = cfg.filters || {};
              setStatusFilter(String(f.status ?? ''));
              setStartDate(String(f.start_date ?? ''));
              setEndDate(String(f.end_date ?? ''));
              setProjectFilter(String(f.project_id ?? ''));
              setEmployeeFilter(String(f.employee_id ?? ''));
              setDepartmentFilter(String(f.department_id ?? ''));
              setManagerFilter(String(f.manager_id ?? ''));
              setTaskTypeFilter(String(f.task_type_id ?? ''));
              setBillableFilter(String(f.billable ?? ''));
              setEmployeeCategoryFilter(String(f.employee_category ?? ''));
              setEmployeePracticeFilter(String(f.employee_practice ?? ''));
              setActiveInactiveFilter(String(f.active_inactive ?? ''));
            }}
            onActiveFavoriteChange={(name) => setActiveFavoriteName(name)}
          />

          {/* Refresh */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => activeGroupBy.length > 0 ? fetchGroupedData() : fetchFlatData(1)}
            disabled={loading}
          >
            <Refresh className={loading ? 'spin' : ''} />
            Refresh
          </Button>

          {/* Count */}
          <div className="gt-total-display">
            {activeGroupBy.length > 0 ? (
              <span>
                <strong>{totalCount}</strong> entries &middot; <strong>{grandTotalHours.toFixed(2)}</strong> hrs
              </span>
            ) : (
              <span>
                {flatTotal > 0 && <>
                  <strong>{((flatPage - 1) * PER_PAGE) + 1}-{Math.min(flatPage * PER_PAGE, flatTotal)}</strong> of <strong>{flatTotal}</strong>
                </>}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="gt-filters-panel">
          <div className="gt-filters-row">
            <div className="gt-filter-group">
              <label>Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="gt-filter-group">
              <label>End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="gt-filter-group">
              <label>Project</label>
              <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <option value="">All Projects</option>
                {filterProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Employee</label>
              <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
                <option value="">All Employees</option>
                {filterEmployees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Department</label>
              <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                <option value="">All Departments</option>
                {filterDepartments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="gt-filters-row">
            <div className="gt-filter-group">
              <label>Manager</label>
              <select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}>
                <option value="">All Managers</option>
                {filterManagers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Task Type</label>
              <select value={taskTypeFilter} onChange={(e) => setTaskTypeFilter(e.target.value)}>
                <option value="">All Tasks</option>
                {filterTaskTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Billable</label>
              <select value={billableFilter} onChange={(e) => setBillableFilter(e.target.value)}>
                <option value="">All</option>
                <option value="true">Billable</option>
                <option value="false">Non-Billable</option>
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Employee Category</label>
              <select value={employeeCategoryFilter} onChange={(e) => setEmployeeCategoryFilter(e.target.value)}>
                <option value="">All Categories</option>
                {Array.from(new Set(filterEmployees.map((e: any) => e.category).filter(Boolean))).map((cat) => (
                  <option key={String(cat)} value={String(cat)}>{String(cat)}</option>
                ))}
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Employee Practice</label>
              <select value={employeePracticeFilter} onChange={(e) => setEmployeePracticeFilter(e.target.value)}>
                <option value="">All Practices</option>
                {Array.from(new Set(filterEmployees.map((e: any) => e.practice).filter(Boolean))).map((pr) => (
                  <option key={String(pr)} value={String(pr)}>{String(pr)}</option>
                ))}
              </select>
            </div>
            <div className="gt-filter-group">
              <label>Active/Inactive</label>
              <select value={activeInactiveFilter} onChange={(e) => setActiveInactiveFilter(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="gt-filters-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter('');
                setStartDate('');
                setEndDate('');
                setProjectFilter('');
                setEmployeeFilter('');
                setDepartmentFilter('');
                setManagerFilter('');
                setTaskTypeFilter('');
                setBillableFilter('');
                setEmployeeCategoryFilter('');
                setEmployeePracticeFilter('');
                setActiveInactiveFilter('');
              }}
            >
              Clear All
            </Button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && <div className="gt-error">{error}</div>}

      {/* Scrollable Table Container */}
      <div className="gt-table-scroll-container">
        <div className="gt-table-wrapper">
        <table className="gt-table">
          <colgroup>
            {visibleColumns.includes('date') && <col className="col-date" />}
            {visibleColumns.includes('day') && <col className="col-day" />}
            {visibleColumns.includes('employee') && <col className="col-employee" />}
            {visibleColumns.includes('employee_email') && <col className="col-employee-email" />}
            {visibleColumns.includes('employee_code') && <col className="col-employee-code" />}
            {visibleColumns.includes('accounts') && <col className="col-accounts" />}
            {visibleColumns.includes('department') && <col className="col-department" />}
            {visibleColumns.includes('practice') && <col className="col-practice" />}
            {visibleColumns.includes('project') && <col className="col-project" />}
            {visibleColumns.includes('project_manager') && <col className="col-project-manager" />}
            {visibleColumns.includes('project_category') && <col className="col-project-category" />}
            {visibleColumns.includes('resource_category') && <col className="col-resource-category" />}
            {visibleColumns.includes('tasks') && <col className="col-tasks" />}
            {visibleColumns.includes('sub_tasks') && <col className="col-sub-tasks" />}
            {visibleColumns.includes('description') && <col className="col-description" />}
            {visibleColumns.includes('hours') && <col className="col-hours" />}
            {visibleColumns.includes('reject_reason') && <col className="col-reject-reason" />}
            {visibleColumns.includes('status') && <col className="col-status" />}
            {visibleColumns.includes('active_status') && <col className="col-active-status" />}
            <col className="col-settings" />
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.includes('date') && (
                <th onClick={() => handleFlatSort('date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Date
                  <SortIcon direction={getSortDirection('date')} isActive={isColumnSorted('date')} />
                </th>
              )}
              {visibleColumns.includes('day') && (
                <th onClick={() => handleFlatSort('day')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Day
                  <SortIcon direction={getSortDirection('day')} isActive={isColumnSorted('day')} />
                </th>
              )}
              {visibleColumns.includes('employee') && (
                <th onClick={() => handleFlatSort('employee')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Employee
                  <SortIcon direction={getSortDirection('employee')} isActive={isColumnSorted('employee')} />
                </th>
              )}
              {visibleColumns.includes('employee_email') && <th>Employee Email</th>}
              {visibleColumns.includes('employee_code') && <th>Code</th>}
              {visibleColumns.includes('accounts') && <th>Accounts</th>}
              {visibleColumns.includes('department') && <th>Department</th>}
              {visibleColumns.includes('practice') && <th>Practice</th>}
              {visibleColumns.includes('project') && (
                <th onClick={() => handleFlatSort('project')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Project
                  <SortIcon direction={getSortDirection('project')} isActive={isColumnSorted('project')} />
                </th>
              )}
              {visibleColumns.includes('project_manager') && <th>Project Manager</th>}
              {visibleColumns.includes('project_category') && <th>Project Category</th>}
              {visibleColumns.includes('resource_category') && <th>Resource Category</th>}
              {visibleColumns.includes('tasks') && (
                <th onClick={() => handleFlatSort('tasks')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Tasks
                  <SortIcon direction={getSortDirection('tasks')} isActive={isColumnSorted('tasks')} />
                </th>
              )}
              {visibleColumns.includes('sub_tasks') && <th>Sub Tasks</th>}
              {visibleColumns.includes('description') && (
                <th onClick={() => handleFlatSort('description')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Description
                  <SortIcon direction={getSortDirection('description')} isActive={isColumnSorted('description')} />
                </th>
              )}
              {visibleColumns.includes('hours') && (
                <th onClick={() => handleFlatSort('hours')} className="hours-header">
                  Hours Spent
                  <SortIcon direction={getSortDirection('hours')} isActive={isColumnSorted('hours')} />
                </th>
              )}
              {visibleColumns.includes('reject_reason') && <th>Reject Reason</th>}
              {visibleColumns.includes('status') && (
                <th onClick={() => handleFlatSort('status')} className="status-header">
                  Status
                  <SortIcon direction={getSortDirection('status')} isActive={isColumnSorted('status')} />
                </th>
              )}
              {visibleColumns.includes('active_status') && (
                <th onClick={() => handleFlatSort('active_status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Active/Inactive
                  <SortIcon direction={getSortDirection('active_status')} isActive={isColumnSorted('active_status')} />
                </th>
              )}
              {/* Optional columns settings icon */}
              <th className="gt-th-settings" ref={columnMenuRef}>
                <button
                  className="gt-settings-btn"
                  onClick={() => setShowColumnMenu(!showColumnMenu)}
                  title="Toggle columns"
                >
                  <Columns />
                </button>
                {showColumnMenu && (
                  <div className="gt-column-menu">
                    <div className="gt-column-header">
                      <span>Columns</span>
                      <div className="gt-column-header-actions">
                        <button onClick={handleSelectAllColumns}>All</button>
                        <button onClick={handleResetColumns}>Reset</button>
                      </div>
                    </div>
                    <div className="gt-column-body">
                      {TIMESHEET_COLUMNS.map(col => (
                        <label key={col.key} className="gt-column-checkbox">
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(col.key)}
                            onChange={() => handleColumnToggle(col.key)}
                          />
                          <span>{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="gt-loading-cell">Loading...</td>
              </tr>
            )}

            {!loading && activeGroupBy.length > 0 && groups.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="gt-empty-cell">No data found</td>
              </tr>
            )}

            {!loading && activeGroupBy.length > 0 && groups.map((g) => renderGroupNode(g, 0))}

            {!loading && activeGroupBy.length === 0 && flatTimesheets.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="gt-empty-cell">No timesheets found</td>
              </tr>
            )}

            {!loading && activeGroupBy.length === 0 && sortedFlatTimesheets.map((t) => (
              <tr key={t.id} className="detail-row">
                {visibleColumns.includes('date') && <td>{t.date ? formatDisplayDate(t.date) : ''}</td>}
                {visibleColumns.includes('day') && <td>{t.date ? getDayName(t.date) : ''}</td>}
                {visibleColumns.includes('employee') && <td>{t.employee_name}</td>}
                {visibleColumns.includes('employee_email') && <td className="email-cell">{t.employee_email || '-'}</td>}
                {visibleColumns.includes('employee_code') && <td>{t.employee_code || '-'}</td>}
                {visibleColumns.includes('accounts') && <td>{t.client_name || '-'}</td>}
                {visibleColumns.includes('department') && <td>{(t as any).department_name || '-'}</td>}
                {visibleColumns.includes('practice') && <td>{(t as any).employee_practice || '-'}</td>}
                {visibleColumns.includes('project') && <td>{t.project_name}</td>}
                {visibleColumns.includes('project_manager') && <td>{(t as any).project_manager_name || '-'}</td>}
                {visibleColumns.includes('project_category') && <td>{(t as any).project_type || '-'}</td>}
                {visibleColumns.includes('resource_category') && <td>{(t as any).employee_category || '-'}</td>}
                {visibleColumns.includes('tasks') && <td className="tasks-cell">{t.task_type_name}</td>}
                {visibleColumns.includes('sub_tasks') && <td>-</td>}
                {visibleColumns.includes('description') && (
                  <td className="description-cell" title={t.description || ''}>
                    {t.description ? (t.description.length > 60 ? t.description.slice(0, 60) + '...' : t.description) : ''}
                  </td>
                )}
                {visibleColumns.includes('hours') && (
                  <td className="hours-cell">
                    {t.hours.toFixed(2)}
                  </td>
                )}
                {visibleColumns.includes('reject_reason') && <td>-</td>}
                {visibleColumns.includes('status') && (
                  <td className="status-cell">
                    <span className={`status-badge status-${t.status}`}>{t.status}</span>
                  </td>
                )}
                {visibleColumns.includes('active_status') && (
                  <td>{(t as any).employee_is_active === false ? 'Inactive' : 'Active'}</td>
                )}
                <td className="gt-td-settings"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Flat pagination */}
      {activeGroupBy.length === 0 && flatPages > 1 && (
        <div className="gt-pagination">
          <button
            className="pagination-btn"
            disabled={flatPage <= 1}
            onClick={() => fetchFlatData(flatPage - 1)}
          >
            Previous
          </button>
          <span className="pagination-info">Page {flatPage} of {flatPages}</span>
          <button
            className="pagination-btn"
            disabled={flatPage >= flatPages}
            onClick={() => fetchFlatData(flatPage + 1)}
          >
            Next
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

/* ── Helpers ── */

function formatDisplayDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getDayName(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch {
    return '';
  }
}

export default GroupedTimesheets;
